import React from "react";
import { Box, Button, Group, Text } from "@mantine/core";
import { IconArrowsJoin } from "@tabler/icons-react";
import styles from "./MergeSelectionBar.module.css";

interface MergeSelectionBarProps {
  /** Number of clients currently selected. */
  count: number;
  /** Maximum selectable (clients can be merged 2 at a time, up to this cap). */
  max: number;
  /** Minimum required before a merge can start. */
  min?: number;
  onClear: () => void;
  onMerge: () => void;
}

/**
 * Floating action bar that appears when the user has selected clients to merge.
 * Mirrors the design in the clients screenshot: a dark pill with a selection
 * count, a "Clear" text action, and a primary "Merge" button.
 */
const MergeSelectionBar: React.FC<MergeSelectionBarProps> = ({
  count,
  max,
  min = 2,
  onClear,
  onMerge,
}) => {
  if (count === 0) return null;

  const canMerge = count >= min;

  return (
    <Box
      className={styles.container}
      role="region"
      aria-label="Merge selection"
    >
      <Group gap="lg" wrap="nowrap" align="center">
        <Group gap="sm" wrap="nowrap" align="center">
          <Box className={styles.countBadge}>
            {count}/{max}
          </Box>
          <Text c="white" fw={500} size="sm">
            clients selected
          </Text>
        </Group>
        <Group gap="xs" wrap="nowrap">
          <Button variant="subtle" color="gray" size="sm" onClick={onClear}>
            Clear
          </Button>
          <Button
            color="var(--brand-teal)"
            size="sm"
            leftSection={<IconArrowsJoin size={16} />}
            onClick={onMerge}
            disabled={!canMerge}
          >
            Merge
          </Button>
        </Group>
      </Group>
    </Box>
  );
};

export default MergeSelectionBar;
